import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { IMessagesTemplate } from "@/types/messages-template.type";

const API_URL = `${import.meta.env.VITE_API_URL}/marketing`;
const makeRequestDebounced = createDebounce(makeRequest, 200);

export async function getAllMessageTemplates(cursor?: string) {
  const url = new URL(`${API_URL}/message-templates`);
  if (cursor) {
    url.searchParams.append("cursor", cursor);
  }

  const result = (await makeRequestDebounced(url.toString())) as ApiResponseType<{
    ok: boolean, data: {
      name: string,
      id: string,
      components: {
        type: string,
        text: string
      }
    }[], message: string, cursor: string
  }>;

  return result;
}

export async function createMessageTemplate(data: IMessagesTemplate) {
  const url = `${API_URL}/message-templates`;

  const result = (await makeRequest(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })) as ApiResponseType<{ ok: boolean, data: unknown, message: string }>;

  return result;
}

export async function uploadMediaToFacebook(fileUrl: string, filename: string, mimeType: string) {
  const url = `${API_URL}/media/upload-to-facebook`;

  const result = (await makeRequest(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fileUrl, filename, mimeType }),
  })) as ApiResponseType<{ assetHandle: string }>;

  return result;
}

export async function uploadMetaMedia(file: File) {
  // Upload via S3 - backend faz o processamento
  // 1. Solicitar URL assinada do S3
  const presignedUrl = `${API_URL}/media/presigned-url`;

  const presignedResponse = await makeRequest(presignedUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type,
    }),
  }) as ApiResponseType<{ uploadUrl: string; fileUrl: string; fileKey: string }>;

  if (!presignedResponse.ok || !presignedResponse.data) {
    throw new Error("Erro ao gerar URL de upload");
  }

  // 2. Upload direto para S3
  const uploadResponse = await fetch(presignedResponse.data.uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error("Erro ao fazer upload do arquivo para S3");
  }

  // 3. Backend baixa do S3 e envia para o Facebook
  const facebookUpload = await uploadMediaToFacebook(
    presignedResponse.data.fileUrl,
    file.name,
    file.type
  );

  if (!facebookUpload.ok || !facebookUpload.data) {
    throw new Error("Erro ao fazer upload para o Facebook");
  }

  return {
    ok: true,
    data: {
      assetHandle: facebookUpload.data.assetHandle,
    }
  } as ApiResponseType<{ assetHandle: string }>;
}

export async function uploadWhatsAppMedia(file: File) {
  // Upload via S3 - backend faz o processamento e envia para WhatsApp
  const presignedUrl = `${API_URL}/media/presigned-url`;

  const presignedResponse = await makeRequest(presignedUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type,
    }),
  }) as ApiResponseType<{ uploadUrl: string; fileUrl: string; fileKey: string }>;

  if (!presignedResponse.ok || !presignedResponse.data) {
    throw new Error("Erro ao gerar URL de upload");
  }

  const uploadResponse = await fetch(presignedResponse.data.uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error("Erro ao fazer upload do arquivo para S3");
  }

  const whatsappUpload = await makeRequest(`${API_URL}/media/upload-to-whatsapp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      fileUrl: presignedResponse.data.fileUrl,
      filename: file.name,
      mimeType: file.type,
    }),
  }) as ApiResponseType<{ mediaId: string }>;

  if (!whatsappUpload.ok || !whatsappUpload.data) {
    throw new Error("Erro ao fazer upload para o WhatsApp");
  }

  return {
    ok: true,
    data: {
      mediaId: whatsappUpload.data.mediaId,
    }
  } as ApiResponseType<{ mediaId: string }>;
}

export async function sendWhatsAppVideoMessage(to: string, mediaId: string, caption?: string) {
  const url = `${API_URL}/messages/video`;

  const result = (await makeRequest(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ to, mediaId, caption }),
  })) as ApiResponseType<unknown>;

  return result;
}

export async function deleteMessageTemplates(name: string) {
  const url = `${API_URL}/message-templates`

  const result = (await makeRequest(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  })) as ApiResponseType<{ ok: boolean, data: Record<string, string>, message: string }>;

  return result;
}
